import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaXmark, FaSpinner, FaExpand } from 'react-icons/fa6';

/**
 * PlotViewer Component
 *
 * Displays generated plots from the plotting interface
 * with fullscreen viewing and download support
 */

interface PlotViewerProps {
  plotData?: string;
  isVisible: boolean;
  onClose?: () => void;
}

const PlotViewer: React.FC<PlotViewerProps> = ({
  plotData,
  isVisible,
  onClose
}) => {
  const { t } = useTranslation('training');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleDownload = useCallback(() => {
    if (!plotData) return;

    const link = document.createElement('a');
    link.href = plotData;
    link.download = `plot_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();

    // Use setTimeout to safely remove element
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  }, [plotData]);

  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoading(false);
    setImageError(true);
  }, []);

  const openFullscreen = useCallback(() => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
  }, []);

  // Escape closes the fullscreen lightbox.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen, closeFullscreen]);

  if (!isVisible || !plotData) {
    return null;
  }

  return (
    <>
      <div className="border-t border-border bg-muted/30 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs sm:text-sm font-semibold text-foreground tracking-tight">
            {t('training.plotViewer.title')}
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openFullscreen}
              disabled={imageError || imageLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted hover:text-foreground disabled:bg-muted disabled:text-muted-foreground active:scale-[0.98] transition-all duration-150 shadow-sm"
            >
              <FaExpand className="text-[10px]" />
              <span>{t('training.plotViewer.fullscreen', { defaultValue: 'Vollbild' })}</span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={imageError}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground active:scale-[0.98] transition-all duration-150"
            >
              <FaDownload className="text-[10px]" />
              <span>{t('training.plotViewer.download')}</span>
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
                aria-label={t('training.plotViewer.closePlot')}
              >
                <FaXmark className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-auto rounded-lg bg-card border border-border shadow-sm relative group">
          {imageLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent">
                <FaSpinner className="animate-spin text-primary text-xs" />
              </div>
            </div>
          )}

          {imageError && (
            <div className="flex items-center justify-center h-64 bg-red-50/50">
              <p className="text-xs sm:text-sm text-red-500">{t('training.plotViewer.loadError')}</p>
            </div>
          )}

          <div className={`flex justify-center p-2 ${imageLoading || imageError ? 'hidden' : ''}`}>
            <img
              src={plotData}
              alt={t('training.plotViewer.altText')}
              className="max-w-full h-auto rounded-lg max-h-[80vh] cursor-pointer transition-opacity duration-150 hover:opacity-95"
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={openFullscreen}
            />
            <button
              type="button"
              onClick={openFullscreen}
              className="absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-black/70"
              title={t('training.plotViewer.fullscreen', { defaultValue: 'Vollbild' })}
              aria-label={t('training.plotViewer.fullscreen', { defaultValue: 'Vollbild' })}
            >
              <FaExpand className="text-[10px]" />
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('training.plotViewer.altText')}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={closeFullscreen}
        >
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 active:scale-[0.98] transition-all duration-150"
              title={t('training.plotViewer.download')}
              aria-label={t('training.plotViewer.download')}
            >
              <FaDownload className="text-xs" />
            </button>
            <button
              type="button"
              onClick={closeFullscreen}
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 active:scale-[0.98] transition-all duration-150"
              title={t('training.plotViewer.close', { defaultValue: 'Schliessen' })}
              aria-label={t('training.plotViewer.close', { defaultValue: 'Schliessen' })}
            >
              <FaXmark className="text-sm" />
            </button>
          </div>
          <div className="text-center max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={plotData}
              alt={t('training.plotViewer.altText')}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PlotViewer;
